import { AlertCircle, Circle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { 
  PRIORITY_LEVELS, 
  PRIORITY_CONFIG, 
  PriorityLevel, 
  getPriorityFromValue 
} from "@/constants/priority";

interface PrioritySelectorProps {
  value: number | null | undefined;
  onChange: (priority: number) => void;
  disabled?: boolean;
  size?: "sm" | "default";
  className?: string;
}

const priorityIcons: Record<PriorityLevel, React.ReactNode> = {
  high: <AlertCircle className="h-3.5 w-3.5" />,
  medium: <Circle className="h-3.5 w-3.5 fill-current" />,
  low: <Circle className="h-3.5 w-3.5" />,
};

export function PrioritySelector({ 
  value, 
  onChange, 
  disabled = false,
  size = "default",
  className 
}: PrioritySelectorProps) {
  const currentLevel = getPriorityFromValue(value);
  
  const buttonSize = size === "sm" ? "h-8 px-2 text-xs" : "h-10 px-3 text-sm";
  
  return (
    <div className={cn("flex gap-1", className)}>
      {(Object.keys(PRIORITY_LEVELS) as PriorityLevel[]).map((level) => {
        const config = PRIORITY_CONFIG[level];
        const isActive = currentLevel === level;
        
        return (
          <Button
            key={level}
            type="button"
            variant={isActive ? "default" : "outline"}
            size="sm"
            disabled={disabled}
            onClick={() => onChange(PRIORITY_LEVELS[level])}
            className={cn(
              buttonSize,
              "transition-all",
              isActive && level === "high" && "bg-destructive hover:bg-destructive/90 text-destructive-foreground",
              isActive && level === "medium" && "bg-warning hover:bg-warning/90 text-warning-foreground",
              isActive && level === "low" && "bg-muted hover:bg-muted/80 text-muted-foreground",
              !isActive && "hover:bg-muted/50"
            )}
          >
            {priorityIcons[level]}
            <span className="ml-1.5">{config.label}</span>
          </Button>
        );
      })}
    </div>
  );
}

// Badge component for displaying priority in compact form
interface PriorityBadgeProps {
  value: number | null | undefined;
  className?: string;
}

export function PriorityBadge({ value, className }: PriorityBadgeProps) {
  const level = getPriorityFromValue(value);
  const config = PRIORITY_CONFIG[level];
  
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
        config.bgColor,
        config.color,
        className
      )}
    >
      {priorityIcons[level]}
      {config.label}
    </span>
  );
}
